import { type QueryClient, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { AllDiscounts, CreateDiscount } from './api'
import {
  createCustomerBillableDiscount,
  deleteCustomerDiscount,
  getAllBillables,
  getBillableItemPricesOnCustomer,
  getBillableItemPricesOnTemplate,
  getCustomerBillableDiscounts,
  getTemplateOnCustomer,
} from './endpoints'

enum Keys {
  CustomerBillableDiscounts = 'customer-billable-discounts',
  CustomerTemplate = 'customer-template',
  CustomerBillablePrices = 'customer-billable-prices',
  TemplateBillablePrices = 'template-billable-prices',
  AllBillables = 'all-billables',
  DeleteCustomerDiscount = 'delete-customer-discount',
  CreateCustomerDiscount = 'create-customer-discount',
}

export const QueryKeys = {
  CustomerBillableDiscounts: (customerId: string) => [Keys.CustomerBillableDiscounts, customerId] as const,
  CustomerTemplate: (customerId: string) => [Keys.CustomerTemplate, customerId] as const,
  CustomerBillablePrices: (customerId: string) => [Keys.CustomerBillablePrices, customerId] as const,
  TemplateBillablePrices: (templateId: string) => [Keys.TemplateBillablePrices, templateId] as const,
  AllBillables: () => [Keys.AllBillables] as const,
  DeleteCustomerDiscount: () => [Keys.DeleteCustomerDiscount] as const,
  CreateCustomerDiscount: () => [Keys.CreateCustomerDiscount] as const,
}

export const useCustomerTemplate = (customerId: string) => {
  return useQuery({
    queryKey: QueryKeys.CustomerTemplate(customerId),
    queryFn: () => getTemplateOnCustomer(customerId),
  })
}

export const useCustomerBillablePrices = (customerId: string) => {
  return useQuery({
    queryKey: QueryKeys.CustomerBillablePrices(customerId),
    queryFn: () => getBillableItemPricesOnCustomer(customerId),
  })
}

export const useTemplateBillablePrices = (templateId: string | undefined) => {
  return useQuery({
    queryKey: QueryKeys.TemplateBillablePrices(templateId as string),
    queryFn: () => getBillableItemPricesOnTemplate(templateId as string),
    enabled: !!templateId,
  })
}

export const useAllBillables = () => {
  return useQuery({
    queryKey: QueryKeys.AllBillables(),
    queryFn: () => getAllBillables(),
  })
}

export const useCustomerBillableDiscounts = (customerId: string) => {
  return useQuery({
    queryKey: QueryKeys.CustomerBillableDiscounts(customerId),
    queryFn: () => getCustomerBillableDiscounts(customerId),
  })
}

export const useDeleteCustomerDiscount = (customerId: string) => {
  const client = useQueryClient()

  return useMutation({
    mutationKey: QueryKeys.DeleteCustomerDiscount(),
    mutationFn: (billablePriceId: string) => deleteCustomerDiscount(customerId, billablePriceId),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: QueryKeys.CustomerBillableDiscounts(customerId) })
    },
  })
}

export const useCreateCustomerDiscount = <T extends AllDiscounts>(customerId: string) => {
  const client = useQueryClient()

  return useMutation({
    mutationKey: QueryKeys.CreateCustomerDiscount(),
    mutationFn: (params: CreateDiscount<T>) => createCustomerBillableDiscount(customerId, params),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: QueryKeys.CustomerBillableDiscounts(customerId) })
    },
  })
}

interface InvalidateCustomerDiscountsParams {
  queryClient: QueryClient
  customerId: string
}

export const invalidateCustomerDiscounts = ({ queryClient, customerId }: InvalidateCustomerDiscountsParams) => {
  queryClient.invalidateQueries({ queryKey: QueryKeys.CustomerTemplate(customerId) })
  queryClient.invalidateQueries({ queryKey: QueryKeys.CustomerBillablePrices(customerId) })
  queryClient.invalidateQueries({ queryKey: QueryKeys.CustomerBillableDiscounts(customerId) })
  queryClient.invalidateQueries({ queryKey: [Keys.TemplateBillablePrices] })
}
